import Close from '@mui/icons-material/Close';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import Flag from '@mui/icons-material/Flag';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { buttonBaseClasses, buttonClasses, SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { TicketState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { cloneElement, type JSX, useMemo, useRef } from 'react';

import Menu, { MenuHandler } from '~components/Menu/Menu';
import {
  LoadingSpinner,
  SmallButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { Ticket } from '~hooks/useTickets/models';
import { TicketDrawerBusyReason } from '~pages/Approvals/Files/components/TicketDrawer/constants';
import { useStores } from '~store';
import { canEditTicket } from '~utils/tickets/ticket-utils';
import { downloadFile } from '~utils/utilFunctions';

type AvailableAction = {
  icon: JSX.Element;
  isBusy?: boolean;
  isDisabled: boolean;
  label: string;
  onClick: () => Promise<void> | void;
};

interface ActionsColumnProps {
  busyReason?: TicketDrawerBusyReason;
  onDeleteLoadClick?: () => void;
  onTicketFlaggedStatusChangeClick: () => Promise<void>;
  sx?: SxProps<Theme>;
  ticket?: Ticket;
}

export default function ActionsColumn({
  busyReason,
  onDeleteLoadClick,
  onTicketFlaggedStatusChangeClick,
  sx,
  ticket,
}: ActionsColumnProps) {
  const { userStore } = useStores();
  const userPermissions = userStore.getPermissions();

  const menuHandler = useRef<MenuHandler>(null);

  const hasTicket = !!ticket?.id;
  const isDeletingLoad = busyReason === TicketDrawerBusyReason.DELETING_LOAD;

  const dropdownMenuActions = useMemo(() => {
    const ticketHasImage = !!ticket?.imageUrl;

    const availableActions: AvailableAction[] = [
      {
        icon: <FileDownloadOutlined />,
        isDisabled: !ticketHasImage,
        label: t('approvals.files.download_image_cta'),
        onClick: () => {
          if (ticketHasImage) {
            downloadFile(ticket.id, ticket.imageUrl, true);
          }
        },
      },
    ];

    const isTicketEditable =
      userPermissions.canEditTicket &&
      hasTicket &&
      canEditTicket(ticket.state as TicketState);

    if (ticket?.flagged) {
      availableActions.push({
        icon: <FlagOutlined />,
        isBusy: busyReason === TicketDrawerBusyReason.CHANGING_TICKET_FLAGGED_STATE,
        isDisabled: !isTicketEditable,
        label: t('approvals.remove_flag'),
        onClick: onTicketFlaggedStatusChangeClick,
      });
    } else {
      availableActions.push({
        icon: <Flag />,
        isBusy: busyReason === TicketDrawerBusyReason.CHANGING_TICKET_FLAGGED_STATE,
        isDisabled: !isTicketEditable,
        label: t('actions.flag'),
        onClick: onTicketFlaggedStatusChangeClick,
      });
    }

    return availableActions;
  }, [
    busyReason,
    onTicketFlaggedStatusChangeClick,
    ticket,
    userPermissions.canEditTicket,
  ]);

  return (
    <Box sx={{ display: 'flex', gap: 1, ...sx }}>
      <Menu
        ref={menuHandler}
        menuTrigger={
          <SmallButton
            color="brandV2TreadGray3"
            variant="text"
            startIcon={<MoreHoriz />}
            sx={(theme) => ({
              [`&.${buttonBaseClasses.root}`]: {
                border: 'solid 1px transparent',
                borderRadius: '50%',
                height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                minWidth: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                p: 0,
                width: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                '&:hover': { borderColor: theme.brandV2.colors.treadGray3 },
                [`.${buttonClasses.startIcon}`]: { mx: 0 },
              },
            })}
          />
        }
      >
        {dropdownMenuActions.map((action) => (
          <MenuItem
            key={action.label}
            disabled={action.isDisabled || action.isBusy}
            onClick={async (event) => {
              event.stopPropagation();

              if (action.isBusy) {
                return;
              }

              await action.onClick();
              menuHandler.current?.onClose?.();
            }}
          >
            <Box sx={{ alignItems: 'center', display: 'flex', width: '100%', gap: 1 }}>
              {action.isBusy ? (
                <LoadingSpinner
                  isVisible
                  loadingIndicatorSize={15}
                  sx={{ flex: 0, minWidth: '15px' }}
                />
              ) : (
                cloneElement(action.icon, {
                  sx: { ...action.icon.props.sx, fontSize: '16px' },
                })
              )}

              <Typography
                sx={(theme) => ({
                  color: theme.brandV2.colors.treadBlack,
                  fontSize: '12px',
                })}
              >
                {action.label}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {onDeleteLoadClick && (
        <Tooltip
          title={hasTicket ? t('loads.remove_load_and_ticket') : t('loads.remove_load')}
        >
          {isDeletingLoad ? (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                mt: '-15px',
              }}
            >
              <CircularProgress color="secondary" size={15} />
            </Box>
          ) : (
            <Close
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onDeleteLoadClick();
              }}
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray3,
                cursor: 'pointer',
                fontSize: '16px',
                mt: '6px',
                '&:hover': { color: theme.brandV2.colors.treadBlack },
              })}
            />
          )}
        </Tooltip>
      )}
    </Box>
  );
}
